/**
 * Message Actions Component
 * Interactive features for AI responses: copy, regenerate, feedback, expand/collapse
 * Modern, professional design with smooth animations
 */

import React, { useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { Copy, RefreshCw, ThumbsUp, ThumbsDown, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../../../lib/utils';

interface MessageActionsProps {
  messageId: string;
  content: string;
  isLong?: boolean; // True if message is longer than 500 words
  onCopy?: () => void;
  onRegenerate?: () => void;
  onFeedback?: (isPositive: boolean) => void;
  feedbackGiven?: 'positive' | 'negative' | null; // Controlled feedback state from parent
  className?: string;
  isMobileFloating?: boolean; // True when actions appear below message on mobile
}

/**
 * Interactive message actions bar
 * Appears on hover with smooth animations
 */
export const MessageActions: React.FC<MessageActionsProps> = ({
  messageId: _messageId,
  content,
  isLong = false,
  onCopy,
  onRegenerate,
  onFeedback,
  feedbackGiven: feedbackGivenProp, // Controlled feedback state from parent
  className,
  isMobileFloating = false
}) => {
  const [isExpanded, setIsExpanded] = useState(!isLong);
  const [copied, setCopied] = useState(false);
  const [internalFeedback, setInternalFeedback] = useState<'positive' | 'negative' | null>(null);

  // Use controlled feedback if provided, otherwise use internal state
  const feedbackGiven = feedbackGivenProp !== undefined ? feedbackGivenProp : internalFeedback;

  /**
   * Copy message content to clipboard
   */
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      onCopy?.();
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }, [content, onCopy]);

  /**
   * Handle feedback (thumbs up/down)
   */
  const handleFeedback = useCallback((isPositive: boolean) => {
    // Only update internal state if not controlled from parent
    if (feedbackGivenProp === undefined) {
      setInternalFeedback(isPositive ? 'positive' : 'negative');
    }
    onFeedback?.(isPositive);
  }, [onFeedback, feedbackGivenProp]);

  /**
   * Toggle expand/collapse for long messages
   */
  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        // Desktop: Bottom-right corner (ChatGPT style) with smooth positioning
        !isMobileFloating && 'absolute bottom-2 right-2',
        // Mobile floating: Static positioning below message
        isMobileFloating && 'static',
        'flex items-center gap-1.5',
        // Mobile floating: Center align and full width feel
        isMobileFloating && 'justify-center w-full',
        // Modern glassmorphism effect
        'bg-white/98 backdrop-blur-md rounded-xl',
        'shadow-xl border border-gray-200/80',
        'p-1.5 max-md:p-2',
        'z-10', // Ensure actions are above message content
        // Subtle hover effect for desktop (not on mobile floating)
        !isMobileFloating && 'hover:shadow-2xl hover:border-gray-300/80 transition-all duration-200',
        className
      )}
    >
      {/* Copy Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleCopy}
        className={cn(
          'p-2 rounded-md transition-colors',
          'max-md:min-w-[44px] max-md:min-h-[44px] max-md:p-2.5',
          'hover:bg-gray-100 active:bg-gray-200',
          copied && 'bg-emerald-50 text-emerald-600'
        )}
        aria-label={copied ? 'Copied!' : 'Copy message'}
        title={copied ? 'Copied!' : 'Copy message'}
      >
        <Copy className={cn('w-3.5 h-3.5 max-md:w-4 max-md:h-4', copied && 'text-emerald-600')} />
      </motion.button>

      {/* Regenerate Button */}
      {onRegenerate && (
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={onRegenerate}
          className="p-2 rounded-md transition-colors hover:bg-gray-100 active:bg-gray-200"
          aria-label="Regenerate response"
          title="Regenerate response"
        >
          <RefreshCw className="w-3.5 h-3.5 max-md:w-4 max-md:h-4 text-gray-600" />
        </motion.button>
      )}

      {/* Feedback Buttons */}
      {onFeedback && (
        <>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleFeedback(true)}
            className={cn(
              'p-2 rounded-md transition-colors',
              'hover:bg-emerald-50 active:bg-emerald-100',
              feedbackGiven === 'positive' && 'bg-emerald-100 text-emerald-600'
            )}
            aria-label="Thumbs up"
            title="Helpful"
          >
            <ThumbsUp className={cn(
              'w-3.5 h-3.5 max-md:w-4 max-md:h-4',
              feedbackGiven === 'positive' ? 'text-emerald-600' : 'text-gray-600'
            )} />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleFeedback(false)}
            className={cn(
              'p-2 rounded-md transition-colors',
              'hover:bg-red-50 active:bg-red-100',
              feedbackGiven === 'negative' && 'bg-red-100 text-red-600'
            )}
            aria-label="Thumbs down"
            title="Not helpful"
          >
            <ThumbsDown className={cn(
              'w-3.5 h-3.5 max-md:w-4 max-md:h-4',
              feedbackGiven === 'negative' ? 'text-red-600' : 'text-gray-600'
            )} />
          </motion.button>
        </>
      )}

      {/* Expand/Collapse Button (for long messages) */}
      {isLong && (
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleExpand}
          className="p-2 rounded-md transition-colors hover:bg-gray-100 active:bg-gray-200"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          title={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? (
            <ChevronUp className="w-3.5 h-3.5 max-md:w-4 max-md:h-4 text-gray-600" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 max-md:w-4 max-md:h-4 text-gray-600" />
          )}
        </motion.button>
      )}
    </motion.div>
  );
};
